/**
 * iter84.56.8 — Tech-debt split from ReleaseOpsAuditCards.jsx.
 * Mechanical extraction; no behavior change.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    RefreshCw, AlertTriangle, CheckCircle2, 
    Loader2, Trophy,
    Sparkles,
} from "lucide-react";
import { CollapsibleCard } from "@/components/site-admin/CollapsibleCard";

export function OrphanPicksAuditCard({ environment = "preview" }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    const fetchOnce = useCallback(async (showSpinner) => {
        if (showSpinner) setRefreshing(true);
        try {
            const r = await api.releaseOpsOrphanPicksAudit();
            setData(r);
            setError(null);
        } catch (e) {
            setError(e.response?.data?.detail || "Failed to load orphan-picks audit");
        } finally {
            setRefreshing(false);
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchOnce(false); }, [fetchOnce]);

    const grandTotal = data?.grand_total_orphans ?? 0;
    const tournamentsAffected = data?.tournaments_affected ?? 0;
    // iter84.56.4.1 — Verdict:
    //   0 affected → clear (no orphan refs, nothing to clean up)
    //   any affected → warn (operator outreach needed)
    //   API error → fail (auto-open).
    const verdict = error
        ? "fail"
        : grandTotal === 0
            ? "clear"
            : "warn";

    const badge = data
        ? grandTotal === 0
            ? "No orphan picks"
            : `${grandTotal} orphan refs · ${tournamentsAffected} tournaments`
        : (environment === "production" ? "production" : "preview");
    const badgeClass = grandTotal === 0
        ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
        : "bg-amber-500/15 text-amber-300 border-amber-500/40";

    const refreshBtn = (
        <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); fetchOnce(true); }}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); fetchOnce(true); } }}
            data-testid="orphan-picks-refresh-btn"
            className="brutal-btn-ghost text-[10px] py-1 px-2 inline-flex items-center gap-1 cursor-pointer"
        >
            {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Re-scan
        </span>
    );

    const copyDraftIds = (ids) => {
        navigator.clipboard.writeText(ids.join("\n")).then(
            () => toast.success(`Copied ${ids.length} ID${ids.length === 1 ? "" : "s"}`),
            () => toast.error("Copy failed"),
        );
    };

    // iter84.56.4.2 — One-shot heal: patches stage_reached='r32' on
    // every WCKO team with a bracket_slot. Recovers tournaments that
    // were materialized pre-fix (placeholders sitting at 'group' →
    // bracket UI filtered them out).
    const [healing, setHealing] = useState(false);
    const [healResult, setHealResult] = useState(null);
    const runHeal = async () => {
        if (healing) return;
        const ok = window.confirm(
            "Patch stage_reached='r32' on every WCKO team with a bracket_slot? Idempotent — safe to re-run."
        );
        if (!ok) return;
        setHealing(true);
        try {
            const r = await api.releaseOpsHealWckoStages();
            setHealResult(r);
            if (r.tournaments_patched === 0) {
                toast.success("Already healed — no tournaments needed patching.");
            } else {
                toast.success(`Healed ${r.total_teams_patched} teams across ${r.tournaments_patched} tournament(s). Refresh affected brackets to verify.`);
            }
            // Also re-run the orphan audit so the card stays in sync.
            await fetchOnce(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Heal failed");
        } finally {
            setHealing(false);
        }
    };

    // iter84.59.10 — Per-tournament "Prune orphan refs" action. Strips
    // any team_id from drafts.picks + participants.team_preference_ranking
    // that no longer resolves to a real team in the tournament. Lets the
    // operator accept the audit and move on without hand-editing Mongo.
    const [pruningTid, setPruningTid] = useState(null);
    const runPrune = async (tournamentId, tournamentName, totalOrphans) => {
        if (pruningTid) return;
        const ok = window.confirm(
            `Strip ${totalOrphans} orphan ref${totalOrphans === 1 ? "" : "s"} from "${tournamentName}"?\n\n` +
            `Draft picks: team_id will be set to null (slot stays for re-pick).\n` +
            `Participant rankings: orphan team_ids will be removed from the list.\n\n` +
            `Idempotent and audited. Safe to run.`
        );
        if (!ok) return;
        setPruningTid(tournamentId);
        try {
            const r = await api.releaseOpsPruneOrphanRefs(tournamentId);
            toast.success(
                `Pruned ${r.total_refs_stripped} ref${r.total_refs_stripped === 1 ? "" : "s"} ` +
                `(${r.picks_stripped} pick${r.picks_stripped === 1 ? "" : "s"}, ` +
                `${r.rank_entries_stripped} ranking entr${r.rank_entries_stripped === 1 ? "y" : "ies"})`,
            );
            await fetchOnce(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Prune failed");
        } finally {
            setPruningTid(null);
        }
    };

    return (
        <CollapsibleCard
            testId="orphan-picks-audit-card"
            icon={Trophy}
            title="Orphan draft-pick audit"
            badge={badge}
            badgeClass={badgeClass}
            verdict={verdict}
            headerExtra={refreshBtn}
        >
            <div
                data-environment={environment}
                data-grand-total={grandTotal}
                data-tournaments-affected={tournamentsAffected}
            >
                {loading && (
                    <p className="text-xs text-zinc-500 italic flex items-center gap-2">
                        <Loader2 className="w-3 h-3 animate-spin" /> Scanning WCKO tournaments…
                    </p>
                )}
                {error && (
                    <p className="text-xs text-rose-400" data-testid="orphan-picks-audit-error">
                        {error}
                    </p>
                )}
                {!loading && !error && data && (
                    <>
                        <p className="text-[11px] text-zinc-400 leading-relaxed mb-3">
                            {data.note}
                        </p>
                        {/* iter84.56.4.2 — one-shot WCKO stage_reached heal button.
                            Recovers tournaments that have placeholders sitting
                            at stage_reached='group' (pre-fix data) so the bracket
                            UI's STAGE_RANK filter actually shows them in R32. */}
                        <div
                            data-testid="wcko-heal-strip"
                            className="mb-3 p-2 border border-zinc-800 bg-zinc-900/40 rounded-sm text-[11px] flex items-center justify-between gap-3 flex-wrap"
                        >
                            <div className="min-w-0 flex-1">
                                <p className="text-zinc-300 font-bold">Heal WCKO bracket stages</p>
                                <p className="text-[10px] text-zinc-500 leading-relaxed">
                                    One-shot DB patch: sets <code className="text-[9px] bg-zinc-800 px-1 rounded">stage_reached='r32'</code> on every WCKO team with a <code className="text-[9px] bg-zinc-800 px-1 rounded">bracket_slot</code>. Idempotent. Run once per environment after each iter84.56.4 deploy.
                                </p>
                                {healResult && (
                                    <p
                                        data-testid="wcko-heal-result"
                                        className={`mt-1 text-[10px] font-mono ${healResult.total_teams_patched > 0 ? "text-emerald-300" : "text-zinc-500"}`}
                                    >
                                        Last run: {healResult.total_teams_patched} teams patched across {healResult.tournaments_patched} tournament{healResult.tournaments_patched === 1 ? "" : "s"} @ {new Date(healResult.ran_at).toLocaleString()}
                                    </p>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={runHeal}
                                disabled={healing}
                                data-testid="wcko-heal-btn"
                                className="brutal-btn-volt text-[10px] py-1 px-3 inline-flex items-center gap-1 shrink-0 disabled:opacity-50"
                            >
                                {healing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trophy className="w-3 h-3" />}
                                {healing ? "Healing…" : "Heal now"}
                            </button>
                        </div>
                        {grandTotal === 0 ? (
                            <p
                                data-testid="orphan-picks-empty"
                                className="text-[11px] text-emerald-300 italic flex items-center gap-1.5"
                            >
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                Every WCKO draft pick + participant ranking resolves to a real team. No outreach needed.
                            </p>
                        ) : (
                            <ul className="space-y-2" data-testid="orphan-picks-rows">
                                {data.rows.map((row) => (
                                    <li
                                        key={row.tournament_id}
                                        data-testid={`orphan-picks-row-${row.tournament_id}`}
                                        data-total-orphans={row.total_orphans}
                                        className="border border-amber-500/30 bg-amber-500/5 rounded-sm p-2"
                                    >
                                        <div className="flex items-start gap-2 flex-wrap">
                                            <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap text-[11px]">
                                                    <span className="font-bold text-white">{row.tournament_name}</span>
                                                    <code className="text-[9px] text-zinc-500 truncate">{row.tournament_id}</code>
                                                    <span className="text-zinc-400">·</span>
                                                    <span className="text-zinc-300 font-mono">{row.team_count} teams</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => runPrune(row.tournament_id, row.tournament_name, row.total_orphans)}
                                                        disabled={pruningTid === row.tournament_id}
                                                        data-testid={`orphan-picks-prune-${row.tournament_id}`}
                                                        className="brutal-btn-volt text-[9px] py-0.5 px-2 inline-flex items-center gap-1 ml-auto disabled:opacity-50"
                                                        title="Strip orphan team_id refs from this tournament's drafts + participant rankings. Idempotent."
                                                    >
                                                        {pruningTid === row.tournament_id ? (
                                                            <Loader2 className="w-3 h-3 animate-spin" />
                                                        ) : (
                                                            <Sparkles className="w-3 h-3" />
                                                        )}
                                                        {pruningTid === row.tournament_id ? "Pruning…" : "Prune orphans"}
                                                    </button>
                                                </div>
                                                <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-2 text-[10px]">
                                                    <div className="border border-zinc-800 bg-zinc-900/40 rounded-sm p-1.5">
                                                        <div className="flex items-center justify-between mb-0.5">
                                                            <span className="text-zinc-500 uppercase font-bold">Draft picks</span>
                                                            <span className="font-mono text-amber-300 font-bold">
                                                                {row.draft_orphan_picks}
                                                            </span>
                                                        </div>
                                                        {row.affected_draft_ids.length > 0 && (
                                                            <div className="flex items-center justify-between gap-2">
                                                                <ul className="space-y-0.5 flex-1 min-w-0">
                                                                    {row.affected_draft_ids.map((id) => (
                                                                        <li
                                                                            key={id}
                                                                            data-testid={`orphan-picks-draft-${id}`}
                                                                            className="text-[9px] text-zinc-500 font-mono truncate"
                                                                        >
                                                                            · {id}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => copyDraftIds(row.affected_draft_ids)}
                                                                    data-testid={`orphan-picks-row-${row.tournament_id}-copy-draft-ids`}
                                                                    className="text-[9px] text-cyber hover:underline shrink-0"
                                                                >
                                                                    copy
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="border border-zinc-800 bg-zinc-900/40 rounded-sm p-1.5">
                                                        <div className="flex items-center justify-between mb-0.5">
                                                            <span className="text-zinc-500 uppercase font-bold">Ranking refs</span>
                                                            <span className="font-mono text-amber-300 font-bold">
                                                                {row.participant_orphan_refs}
                                                            </span>
                                                        </div>
                                                        {row.affected_participant_ids.length > 0 && (
                                                            <div className="flex items-center justify-between gap-2">
                                                                <ul className="space-y-0.5 flex-1 min-w-0">
                                                                    {row.affected_participant_ids.map((id) => (
                                                                        <li
                                                                            key={id}
                                                                            data-testid={`orphan-picks-participant-${id}`}
                                                                            className="text-[9px] text-zinc-500 font-mono truncate"
                                                                        >
                                                                            · {id}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => copyDraftIds(row.affected_participant_ids)}
                                                                    data-testid={`orphan-picks-row-${row.tournament_id}-copy-participant-ids`}
                                                                    className="text-[9px] text-cyber hover:underline shrink-0"
                                                                >
                                                                    copy
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                        <p className="text-[10px] text-zinc-500 italic mt-2 font-mono">
                            scanned {new Date(data.scanned_at).toLocaleString()}
                        </p>
                    </>
                )}
            </div>
        </CollapsibleCard>
    );
}
